import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Laptop, Loader2, LogOut, Smartphone, Trash2 } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../ui/alert-dialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmt = (iso) => (iso ? new Date(iso).toLocaleString('tr-TR', { dateStyle: 'short', timeStyle: 'short' }) : '-');
const isMobile = (agent = '') => /Android|iOS/.test(agent);

const DeviceRow = ({ d, onRemove, removing }) => {
  const Icon = isMobile(d.agent) ? Smartphone : Laptop;
  return (
    <li className={`flex items-start gap-3 rounded-lg border p-3 ${d.current ? 'border-emerald-200 bg-emerald-50/40' : ''}`} data-testid={`device-row-${d.id}`}>
      <Icon className="h-5 w-5 mt-0.5 text-slate-500 shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm font-medium text-slate-800">{d.agent || 'Bilinmeyen cihaz'}</span>
          {d.current && <Badge className="bg-emerald-100 text-emerald-700" data-testid="device-current-badge">Bu cihaz</Badge>}
          {d.count > 1 && <Badge className="bg-slate-100 text-slate-600 hover:bg-slate-100" data-testid={`device-session-count-${d.id}`}>{d.count} oturum</Badge>}
        </div>
        <p className="text-xs text-slate-500 mt-0.5">
          Son giriş: {fmt(d.last_seen)}{d.ip ? ` · IP: ${d.ip}` : ''}{d.first_seen ? ` · İlk görülme: ${fmt(d.first_seen)}` : ''}
        </p>
      </div>
      {!d.current && (
        <Button size="sm" variant="ghost" onClick={() => onRemove(d)} disabled={removing} className="text-rose-600 hover:text-rose-700 hover:bg-rose-50" data-testid={`device-remove-${d.id}`}>
          {removing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}<span className="ml-1">Kaldır</span>
        </Button>
      )}
    </li>
  );
};

export const DevicesCard = () => {
  const [items, setItems] = useState(null);
  const [busy, setBusy] = useState(null);
  const [confirmOthers, setConfirmOthers] = useState(false);

  const load = useCallback(() => {
    axios.get(`${API}/auth/devices`).then(res => setItems(res.data.items)).catch(() => setItems([]));
  }, []);
  useEffect(() => { load(); }, [load]);

  const remove = async (d) => {
    setBusy(d.id);
    try {
      const res = await axios.delete(`${API}/auth/devices/${d.id}`);
      toast.success(res.data.message);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Cihaz kaldırılamadı');
    } finally { setBusy(null); }
  };

  const logoutOthers = async () => {
    setBusy('others');
    try {
      const res = await axios.post(`${API}/auth/devices/logout-others`);
      toast.success(res.data.message);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'İşlem başarısız');
    } finally { setBusy(null); setConfirmOthers(false); }
  };

  const others = (items || []).filter(d => !d.current).length;

  return (
    <Card className="border-slate-200" data-testid="devices-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2"><Laptop className="h-5 w-5" />Kayıtlı Cihazlar</CardTitle>
        <CardDescription>Hesabınıza giriş yapılan cihazlar. Tanımadığınız bir cihazı kaldırdığınızda o cihazdaki oturum anında kapanır.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {items === null && <Loader2 className="h-5 w-5 animate-spin text-slate-400" />}
        {items?.length === 0 && <p className="text-sm text-slate-500" data-testid="devices-empty">Henüz kayıtlı cihaz yok.</p>}
        {items?.length > 0 && (
          <ul className="space-y-2" data-testid="devices-list">
            {items.map(d => <DeviceRow key={d.id} d={d} onRemove={remove} removing={busy === d.id} />)}
          </ul>
        )}
        {others > 0 && (
          <Button variant="outline" className="w-full text-rose-600 border-rose-200 hover:bg-rose-50" onClick={() => setConfirmOthers(true)} disabled={busy === 'others'} data-testid="devices-logout-others">
            <LogOut className="h-4 w-4 mr-2" />Bu cihaz dışındaki tüm cihazlardan çıkış yap ({others})
          </Button>
        )}
      </CardContent>
      <AlertDialog open={confirmOthers} onOpenChange={setConfirmOthers}>
        <AlertDialogContent data-testid="devices-logout-others-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Diğer cihazlardan çıkış yapılsın mı?</AlertDialogTitle>
            <AlertDialogDescription>Bu cihaz dışındaki {others} cihazdaki oturum hemen kapanacak. Bu cihazlardan tekrar giriş yapıldığında e-posta ile bilgilendirileceksiniz.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="devices-logout-others-cancel">Vazgeç</AlertDialogCancel>
            <AlertDialogAction onClick={logoutOthers} className="bg-rose-600 hover:bg-rose-700" data-testid="devices-logout-others-confirm">Çıkış Yap</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
};
